/**
 * <s-skin-quiz> — "What does my skin need?" quiz
 *
 * Reads the answers from the quiz form and reveals the matching product in
 * each routine step. All product markup is rendered by Liquid; this element
 * only toggles visibility, so the mapping lives entirely in the template.
 *
 * Markup contract:
 *   [data-quiz-form]     form with one radio group per question
 *   [data-quiz-result]   result panel, hidden until the form is submitted
 *   [data-slot]          a routine step
 *   [data-match]         a product inside a step, as "question=answer,answer&question=answer";
 *                        the first product in a step whose clauses all match is shown
 *   [data-show-when]     "question:answer" condition on a fieldset or step; it is
 *                        only used when that answer is chosen (fieldsets are disabled
 *                        otherwise, so their required radios do not block submit)
 *   [data-step-number]   step number, renumbered over the visible steps
 *   [data-quiz-restart]  button that returns to the questions
 */
class SSkinQuiz extends HTMLElement {
  #ac;
  #form;
  #result;

  connectedCallback() {
    this.#form = this.querySelector('[data-quiz-form]');
    this.#result = this.querySelector('[data-quiz-result]');
    if (!this.#form || !this.#result) return;

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#form.addEventListener('change', () => this.#syncConditionalQuestions(), { signal });
    this.#form.addEventListener('submit', (event) => this.#showResult(event), { signal });
    this.querySelector('[data-quiz-restart]')?.addEventListener('click', () => this.#restart(true), { signal });
    this.closest('dialog')?.addEventListener('close', () => this.#restart(false), { signal });

    this.#syncConditionalQuestions();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #conditionMet(element, answers) {
    const condition = element.dataset.showWhen;
    if (!condition) return true;
    const [question, answer] = condition.split(':');
    return answers.get(question) === answer;
  }

  // "skin=oily,combination&issue=blemishes": every clause must match one of its answers.
  #matches(rule, answers) {
    return rule.split('&').every((clause) => {
      const [question, values] = clause.split('=');
      return values.split(',').includes(answers.get(question));
    });
  }

  #syncConditionalQuestions() {
    const answers = new FormData(this.#form);
    for (const fieldset of this.#form.querySelectorAll('fieldset[data-show-when]')) {
      fieldset.disabled = !this.#conditionMet(fieldset, answers);
    }
  }

  #showResult(event) {
    event.preventDefault();
    const answers = new FormData(this.#form);
    let stepNumber = 0;

    for (const slot of this.#result.querySelectorAll('[data-slot]')) {
      let hasMatch = false;
      const slotActive = this.#conditionMet(slot, answers);
      for (const item of slot.querySelectorAll('[data-match]')) {
        // Candidates are ordered by priority: only the first full match is shown.
        const isMatch = slotActive && !hasMatch && this.#matches(item.dataset.match, answers);
        item.hidden = !isMatch;
        if (isMatch) hasMatch = true;
      }
      slot.hidden = !hasMatch;
      if (hasMatch) {
        stepNumber += 1;
        const number = slot.querySelector('[data-step-number]');
        if (number) number.textContent = `${stepNumber}.`;
      }
    }

    this.#form.hidden = true;
    this.#result.hidden = false;
    this.#result.focus();
  }

  #restart(moveFocus) {
    this.#form.reset();
    this.#syncConditionalQuestions();
    this.#form.hidden = false;
    this.#result.hidden = true;
    if (moveFocus) this.#form.querySelector('input')?.focus();
  }
}

if (!customElements.get('s-skin-quiz')) {
  customElements.define('s-skin-quiz', SSkinQuiz);
}
